import React from "react";
import PropTypes from "prop-types";
import { SIGNOL_COLOUR_NAMES } from "@signolteam/shared/colours";
import Icon from "./Icon";
import Loader from "./Loader";
import AppUtils from "@src/utils/appUtils";
import { useIsMobileWidth } from "@src/utils/mediaQuery";

const Button = (props) => {
  const {
    color = "primary",
    id,
    name,
    submit = false,
    disabled = false,
    className = "",
    onClick,
    children,
    padding = true,
    innerPadding = true,
    icon,
    loading = false,
    block = true,
    secondary = false,
    tertiary = false,
  } = props;

  const isMobile = useIsMobileWidth();

  const textStyle =
    !secondary && !tertiary
      ? "text-white"
      : tertiary
        ? `text-${color}-500`
        : `text-${color}-800`;

  const paddingStyle = padding ? "my-2 md:my-4 px-4" : "";

  const disabledStyle = `cursor-not-allowed text-white bg-neutral-100 rounded-2xl`;
  const loadingStyle = `cursor-not-allowed`;
  const enabledStyle = !tertiary
    ? `cursor-pointer shadow hover:shadow-lg/20 active:bg-${color}-800 transition-all hover:-translate-y-px active:translate-y-px`
    : "cursor-pointer";
  const typeStyle = !tertiary
    ? `bg-${color}-${!!secondary ? 100 : 700} hover:bg-${color}-${!!secondary ? 200 : 600} rounded-2xl ${secondary ? `border border-${color}-500` : ""}`
    : "";
  const stateStyle = disabled
    ? disabledStyle
    : loading
      ? `${loadingStyle} ${typeStyle}`
      : `${enabledStyle} ${typeStyle}`;

  return (
    <div
      className={`${className} ${paddingStyle} text-center mx-auto ${block || isMobile ? "block w-full" : "inline-block min-w-40"}`}
    >
      <button
        id={id}
        name={name}
        type={submit ? "submit" : "button"}
        disabled={disabled || loading}
        className={`w-full 
          ${innerPadding ? "md:px-6 py-4" : "py-1"}
          ${textStyle}
          font-semibold focus:outline-none transition duration-200 ease-in-out
          ${stateStyle}
        `}
        onClick={onClick}
      >
        {!!loading ? (
          <Loader size={"xs"} />
        ) : (
          <div className="px-2">
            {icon && (
              <div className="flex justify-center items-center">
                <Icon
                  name={icon}
                  backgroundColor="transparent"
                  size="lg"
                  className="mr-1.5"
                  textColor={
                    !!secondary && !disabled ? `${color}-800` : "white"
                  }
                  centered={false}
                  padding={false}
                />
                <div className="my-auto">{children}</div>
              </div>
            )}
            {!icon && children}
          </div>
        )}
      </button>
    </div>
  );
};

export const buttonControls = {
  id: {
    propType: PropTypes.string,
    disable: true,
    description: "Button ID",
  },
  name: {
    propType: PropTypes.string,
    disable: true,
    description: "Button name",
  },
  type: {
    propType: PropTypes.string,
    controlType: "text",
    description: "Button type",
  },
  disabled: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description: "Disable button",
  },
  padding: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description: "Enable padding",
  },
  textColor: {
    propType: PropTypes.string,
    controlType: "select",
    options: SIGNOL_COLOUR_NAMES,
    description: "Button text color",
  },
  color: {
    propType: PropTypes.string,
    controlType: "select",
    options: SIGNOL_COLOUR_NAMES,
    description: "Button color",
  },
  onClick: {
    propType: PropTypes.func,
    disable: true,
    description: "Button click event",
  },
  children: {
    propType: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
    disable: true,
    description: "Button content",
  },
  icon: {
    propType: PropTypes.string,
    controlType: "select",
    options: ["chevronLeft", "chevronRight"],
    description: "Button icon",
  },
  loading: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description: "Enable showing a loading spinner when passed and true",
  },
  block: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description:
      "if true, will always be full width (instead of just on mobile)",
    defaultValue: true,
  },
};

Button.propTypes = AppUtils.getPropTypeFromControls(buttonControls);

export default Button;
